/**
 * Publish/unpublish chapters.
 *
 * Two flavors:
 *
 * 1. `publishChapterSection` / `unpublishChapterSection` — for ^-marked sections.
 *    Adds / removes the section's headingBlockId from source.publishedSections.
 *    Encrypted glyphs are encrypted client-side — with the passage's own sentence,
 *    or the character key — and merged into the source's encryptedSections on publish.
 *
 * 2. `publishChapter` / `unpublishChapter` — whole-file publish (no ^ sections).
 *    Just flips source.published.
 */

import { extractSectionFromChapterSectionHeading } from '@shared/ast/extractChapterSection'

import { apiService } from './api'
import { ContentAST, InlineContent } from '../transforms/ast'
import { GroupedSection, groupMarkedSectionsFromAST } from '../utils/astContentMarking'
import { encrypt } from '../utils/encryption'

export interface PublishResult {
  success: boolean
  message?: string
}

/**
 * Three kinds of encryption, two keys.
 *
 * A sentence-locked passage is encrypted with the sentence the author will one day
 * publish. The other two — a character's passage, and one the author holds until he is
 * ready — are never opened by a key at all: they open when their holder says so, and
 * their glyphs exist only to be looked at. Those share one key that is never published,
 * because publishing the sentence must not open anything but its own passage.
 */
export interface EncryptionKeys {
  /** Sentence per locked passage, keyed by its encryption id. */
  lockPhrases?: Record<string, string>
  /** For passages no key opens. Never published. */
  withheldGlyphKey?: string
}

function keyFor(group: GroupedSection, keys: EncryptionKeys): string | undefined {
  return group.withheld ? keys.withheldGlyphKey : keys.lockPhrases?.[group.sectionId]
}

function encryptFragments(fragments: { content: unknown }[], passphrase: string): string[] {
  return fragments.map((f) => {
    const plaintext = (f.content as InlineContent[])
      .filter((n: any) => n.type === 'text')
      .map((n: any) => n.text)
      .join('')
    return encrypt(plaintext, passphrase)
  })
}

interface EncryptedSectionPayload {
  sectionId: string
  contentHash: string
  lastModified: string
  encryptedGlyphs: string[]
}

export async function publishChapter(
  bookId: string,
  chapterId: string,
  authorAST: ContentAST,
  keys: EncryptionKeys,
): Promise<PublishResult> {
  const groups = groupMarkedSectionsFromAST(authorAST)

  const encryptedSections: EncryptedSectionPayload[] = []

  if (groups.length > 0) {
    for (const g of groups) {
      const key = keyFor(g, keys)
      if (!key) {
        return {
          success: false,
          message: g.withheld
            ? 'No withheld-passage key on this book yet — reopen Book Settings to generate one'
            : 'This passage has no sentence yet — right-click it and set one',
        }
      }
      encryptedSections.push({
        sectionId: g.sectionId,
        contentHash: g.contentHash,
        lastModified: new Date().toISOString(),
        encryptedGlyphs: encryptFragments(g.fragments, key),
      })
    }
  }

  await apiService.updateChapter(bookId, chapterId, {
    published: true,
    ...(encryptedSections.length > 0 && { encryptedSections }),
  })

  return { success: true }
}

export async function unpublishChapter(bookId: string, chapterId: string): Promise<PublishResult> {
  try {
    await apiService.updateChapter(bookId, chapterId, {
      published: false,
      encryptedSections: [],
    })

    return { success: true }
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Failed to unpublish chapter',
    }
  }
}

/**
 * Publish a ^-marked chapter section. Extracts the slice from the source AST,
 * encrypts any marked sections inside it client-side, then sends one request to
 * add the headingBlockId to source.publishedSections and merge the encrypted
 * glyphs into source.encryptedSections.
 */
export async function publishChapterSection(
  bookId: string,
  sourceFilename: string,
  headingBlockId: string,
  sourceAuthorAST: ContentAST,
  keys: EncryptionKeys,
): Promise<PublishResult> {
  const extracted = extractSectionFromChapterSectionHeading(sourceAuthorAST, headingBlockId)
  if (!extracted) {
    return { success: false, message: 'Could not extract chapter section from source' }
  }

  const groups = groupMarkedSectionsFromAST(extracted.slice)
  const encryptedSections: EncryptedSectionPayload[] = []

  if (groups.length > 0) {
    for (const g of groups) {
      const key = keyFor(g, keys)
      if (!key) {
        return {
          success: false,
          message: g.withheld
            ? 'No withheld-passage key on this book yet — reopen Book Settings to generate one'
            : 'This passage has no sentence yet — right-click it and set one',
        }
      }
      encryptedSections.push({
        sectionId: g.sectionId,
        contentHash: g.contentHash,
        lastModified: new Date().toISOString(),
        encryptedGlyphs: encryptFragments(g.fragments, key),
      })
    }
  }

  try {
    const res = await apiService.publishChapterFromMarkedSection(bookId, {
      sourceFilename,
      headingBlockId,
      ...(encryptedSections.length > 0 && { encryptedSections }),
    })
    if (!res.success) {
      return { success: false, message: res.message || 'Failed to publish chapter section' }
    }
    return { success: true }
  } catch (err: any) {
    return { success: false, message: err?.message || 'Failed to publish chapter section' }
  }
}

/**
 * Unpublish a ^-marked chapter section: removes headingBlockId from
 * source.publishedSections. Encrypted glyphs on the source are kept — they are
 * what the toggle renders, and they stay stable across re-publish.
 */
export async function unpublishChapterSection(
  bookId: string,
  sourceFilename: string,
  headingBlockId: string,
): Promise<PublishResult> {
  try {
    const res = await apiService.unpublishChapterSection(bookId, { sourceFilename, headingBlockId })
    if (!res.success) {
      return { success: false, message: res.message || 'Failed to unpublish chapter section' }
    }
    return { success: true }
  } catch (err: any) {
    return { success: false, message: err?.message || 'Failed to unpublish chapter section' }
  }
}
